import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2, Save, Shield, Lock, ToggleLeft, ToggleRight, CheckCircle2, XCircle, Eye, ShieldCheck } from 'lucide-react';

/* ========== ACCESS CONTROL TAB ========== */
export const AccessControlTab = ({ permissions, loading, saving, onSave, onRefresh, isOwner }) => {
  const [localPerms, setLocalPerms] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (permissions?.apps) {
      setLocalPerms(JSON.parse(JSON.stringify(permissions.apps)));
      setDirty(false);
    }
  }, [permissions]);

  if (loading || !localPerms) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#0B3D91]" />
      </div>
    );
  }

  const tiers = permissions?.available_tiers || ['free', 'starter', 'growth', 'enterprise'];
  const appDefs = permissions?.app_definitions || {};
  const appIds = Object.keys(appDefs).filter(id => id !== 'admin_portal');

  const toggleTier = (appId, tier) => {
    const updated = { ...localPerms };
    const current = updated[appId]?.allowed_tiers || [];
    if (current.includes(tier)) {
      updated[appId] = { ...updated[appId], allowed_tiers: current.filter(t => t !== tier) };
    } else {
      updated[appId] = { ...updated[appId], allowed_tiers: [...current, tier] };
    }
    setLocalPerms(updated);
    setDirty(true);
  };

  const toggleAppEnabled = (appId) => {
    const updated = { ...localPerms };
    updated[appId] = { ...updated[appId], enabled: !updated[appId]?.enabled };
    setLocalPerms(updated);
    setDirty(true);
  };

  const handleSave = () => {
    const changes = {};
    for (const appId of Object.keys(localPerms)) {
      changes[appId] = {
        enabled: localPerms[appId].enabled,
        allowed_tiers: localPerms[appId].allowed_tiers,
        allowed_roles: localPerms[appId].allowed_roles
      };
    }
    onSave(changes);
    setDirty(false);
  };

  const tierColors = {
    free: 'bg-slate-100 text-slate-700 border-slate-200',
    starter: 'bg-blue-100 text-blue-700 border-blue-200',
    growth: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    enterprise: 'bg-purple-100 text-purple-700 border-purple-200'
  };

  const tierActiveColors = {
    free: 'bg-slate-600 text-white border-slate-600',
    starter: 'bg-blue-600 text-white border-blue-600',
    growth: 'bg-emerald-600 text-white border-emerald-600',
    enterprise: 'bg-purple-600 text-white border-purple-600'
  };

  return (
    <div className="space-y-6" data-testid="access-control-tab">
      {/* Role Hierarchy */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Role Hierarchy
        </h3>
        <div className="grid sm:grid-cols-4 gap-3">
          {[
            { id: 'user', level: 1, label: 'User', desc: 'Standard access. Governed by tier & app permissions.', color: 'bg-slate-50 border-slate-200' },
            { id: 'manager', level: 2, label: 'Manager', desc: 'Team management. Can manage team members & usage.', color: 'bg-blue-50 border-blue-200' },
            { id: 'admin', level: 3, label: 'Admin', desc: 'Platform admin. Users, permissions, billing.', color: 'bg-amber-50 border-amber-200' },
            { id: 'owner', level: 4, label: 'Owner', desc: 'Full access. Can manage admins and everything.', color: 'bg-purple-50 border-purple-200' }
          ].map((role) => (
            <div key={role.id} className={`p-4 rounded-xl border ${role.color}`} data-testid={`role-card-${role.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-slate-400">L{role.level}</span>
                <h4 className="font-semibold text-slate-900">{role.label}</h4>
              </div>
              <p className="text-xs text-slate-500">{role.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* App Access Matrix */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] flex items-center gap-2">
            <Lock className="w-5 h-5" />
            App Access Matrix
          </h3>
          <div className="flex items-center gap-2">
            {dirty && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Unsaved changes</span>}
            <Button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="bg-[#0B3D91]"
              data-testid="save-permissions-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Permissions
            </Button>
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-4">
          Control which subscription tiers can access each application. Admins and Owners always have full access.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="access-matrix-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase w-[200px]">Application</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase w-[80px]">Status</th>
                {tiers.map(tier => (
                  <th key={tier} className="text-center p-3 text-xs font-semibold text-slate-500 uppercase">{tier}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {appIds.map((appId) => {
                const app = appDefs[appId] || {};
                const perm = localPerms[appId] || {};
                const isEnabled = perm.enabled !== false;
                return (
                  <tr key={appId} className={`${!isEnabled ? 'opacity-50' : ''} hover:bg-slate-50/50 transition-opacity`} data-testid={`access-row-${appId}`}>
                    <td className="p-3">
                      <p className="font-medium text-slate-900 text-sm">{app.label || appId}</p>
                      <p className="text-xs text-slate-400">{app.description}</p>
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => toggleAppEnabled(appId)}
                        className="mx-auto"
                        data-testid={`toggle-app-${appId}`}
                        title={isEnabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                      >
                        {isEnabled ? (
                          <ToggleRight className="w-7 h-7 text-emerald-500" />
                        ) : (
                          <ToggleLeft className="w-7 h-7 text-slate-300" />
                        )}
                      </button>
                    </td>
                    {tiers.map(tier => {
                      const isActive = (perm.allowed_tiers || []).includes(tier);
                      return (
                        <td key={tier} className="p-3 text-center">
                          <button
                            onClick={() => isEnabled && toggleTier(appId, tier)}
                            disabled={!isEnabled}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-all ${
                              isActive
                                ? tierActiveColors[tier]
                                : `${tierColors[tier]} hover:opacity-80`
                            }`}
                            data-testid={`access-${appId}-${tier}`}
                            title={isActive ? `${tier}: allowed` : `${tier}: blocked`}
                          >
                            {isActive ? (
                              <CheckCircle2 className="w-4 h-4" />
                            ) : (
                              <XCircle className="w-4 h-4 opacity-40" />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Access granted</span>
          <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-slate-300" /> Access blocked</span>
          <span>Owner & Admin roles bypass all tier restrictions</span>
        </div>
      </div>

      {/* Admin Portal Access */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-3 flex items-center gap-2">
          <Eye className="w-5 h-5" />
          Admin Portal Access
        </h3>
        <p className="text-sm text-slate-500 mb-3">
          The Admin Portal is role-gated (not tier-gated). Only <strong>Admin</strong> and <strong>Owner</strong> roles can access it.
        </p>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-sm font-medium">Admin</span>
          <span className="px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 text-sm font-medium">Owner</span>
        </div>
        {isOwner && (
          <p className="text-xs text-emerald-600 mt-3 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            You are the platform Owner. You can promote users to Admin or Owner roles from the Users tab.
          </p>
        )}
      </div>
    </div>
  );
};
